import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import NonLoggedInLayout from '../../layout/NonLoggedInLayout';
import GoogleLoginButton from '../common/GoogleLoginButton';
import AuthContext from '../../context/AuthContext';
import { useToast } from '../../hook/useToast';

const COMMUNITY_PARTNER_REFERRAL_KEY = 'community-partner-referral';

const getStoredCommunityPartnerReferral = () => {
  try {
    return window.sessionStorage.getItem(COMMUNITY_PARTNER_REFERRAL_KEY) || '';
  } catch {
    return '';
  }
};

const storeCommunityPartnerReferral = (referral) => {
  try {
    if (referral) {
      window.sessionStorage.setItem(COMMUNITY_PARTNER_REFERRAL_KEY, referral);
    } else {
      window.sessionStorage.removeItem(COMMUNITY_PARTNER_REFERRAL_KEY);
    }
  } catch {
    // The query parameter still carries the referral when storage is unavailable.
  }
};

const RoleLogin = ({ forcedRole, redirectPath, communityPartnerReferral: referralProp }) => {
  const { showToast } = useToast();
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const requestedRole = forcedRole || searchParams.get('role') || 'Student';
  const requestedCommunityPartnerReferral =
    referralProp?.trim() || searchParams.get('community_partner')?.trim() || '';
  const [communityPartnerReferral, setCommunityPartnerReferral] = useState(
    () => requestedCommunityPartnerReferral || getStoredCommunityPartnerReferral(),
  );
  const [selectedRole, setSelectedRole] = useState(requestedRole);
  const afterLoginPath =
    redirectPath || (selectedRole === 'Parent' ? '/parent-portal/dashboard' : '/council');

  useEffect(() => {
    setSelectedRole(requestedRole);
  }, [requestedRole]);

  useEffect(() => {
    if (!requestedCommunityPartnerReferral) return;
    setCommunityPartnerReferral(requestedCommunityPartnerReferral);
    storeCommunityPartnerReferral(requestedCommunityPartnerReferral);
  }, [requestedCommunityPartnerReferral]);

  const handleRoleChange = (event) => {
    const nextRole = event.target.value;
    setSelectedRole(nextRole);

    if (nextRole !== requestedRole || forcedRole) {
      navigate(`/login?role=${nextRole}`);
    }
  };

  const handleGoogleLogin = (token) => {
    if (selectedRole === 'Student' && communityPartnerReferral) {
      storeCommunityPartnerReferral('');
      setCommunityPartnerReferral('');
    }
    showToast('Login Success', 'success');
    login(token.access, selectedRole);
    navigate(afterLoginPath);
  };

  return (
    <NonLoggedInLayout>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: 450,
            p: 4,
            borderRadius: '16px',
            backgroundColor: '#fff',
          }}
        >
          <Box sx={{ width: '150px' }}>
            <Typography
              sx={{
                fontSize: '40px',
                fontWeight: 700,
                color: '#F6A03A',
                letterSpacing: '1px',
              }}
            >
              HELLO!
            </Typography>
            <Divider sx={{ width: '70%', border: '3px solid #3b3a3a' }} />
          </Box>

          <Typography
            sx={{
              fontSize: '14px',
              color: 'rgba(129, 143, 171, 1)',
              mt: 3,
              fontWeight: 500,
            }}
          >
            Choose your role and continue securely with Google.
          </Typography>

          <Box sx={{ mt: 3 }}>
            <FormControl
              component="fieldset"
              sx={{
                width: '100%',
                mb: 2.5,
                px: 1.5,
                py: 1.25,
                border: '1px solid #E8EAF0',
                borderRadius: '12px',
                backgroundColor: '#FAFBFD',
              }}
            >
              <FormLabel
                component="legend"
                sx={{
                  color: '#27315F',
                  fontSize: '13px',
                  fontWeight: 700,
                  mb: 0.25,
                  '&.Mui-focused': { color: '#27315F' },
                }}
              >
                Login as
              </FormLabel>
              <RadioGroup
                row
                name="portal-role"
                value={selectedRole}
                onChange={handleRoleChange}
                sx={{
                  justifyContent: 'space-between',
                  gap: 1,
                  '& .MuiFormControlLabel-root': { flex: 1, m: 0, minWidth: 0 },
                  '& .MuiFormControlLabel-label': {
                    color: '#596174',
                    fontSize: '13px',
                    fontWeight: 600,
                  },
                }}
              >
                <FormControlLabel
                  value="Student"
                  control={(
                    <Radio
                      size="small"
                      sx={{ color: '#F6A03A', '&.Mui-checked': { color: '#F6A03A' } }}
                    />
                  )}
                  label="Student"
                />
                <FormControlLabel
                  value="Parent"
                  control={(
                    <Radio
                      size="small"
                      sx={{ color: '#F6A03A', '&.Mui-checked': { color: '#F6A03A' } }}
                    />
                  )}
                  label="Parent"
                />
              </RadioGroup>
            </FormControl>

            <GoogleLoginButton
              onSuccessLogin={handleGoogleLogin}
              communityPartnerReferral={
                selectedRole === 'Student' ? communityPartnerReferral : ''
              }
            />
          </Box>
        </Box>
      </Box>
    </NonLoggedInLayout>
  );
};

RoleLogin.propTypes = {
  forcedRole: PropTypes.string,
  redirectPath: PropTypes.string,
  communityPartnerReferral: PropTypes.string,
};

RoleLogin.defaultProps = {
  forcedRole: '',
  redirectPath: '',
  communityPartnerReferral: '',
};

export default RoleLogin;
